import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { CircularProgress, Box } from '@mui/material';
import PageContent from 'layouts/PageContent';
import RolesAndPermissionForm from 'components/role-and-permissions/RolesAndPermissionForm';
import RoleInformationForm from 'components/role-and-permissions/StepperForm/RoleInformationForm';
import RolePermissionForm from 'components/role-and-permissions/StepperForm/RolePermissionForm';
import { roleInformationSchema } from 'components/role-and-permissions/StepperForm/RoleInformationForm';

import { useForm } from 'react-hook-form';
import useStepper from 'hooks/useStepper';
import { buildRoute } from 'routes';
import {
  getRoleAndPermission,
  getRoleMenus,
  updateRolesAndPermissions,
  getMenuDetails,
  createRoleClaim,
} from 'app/features/roles-and-permissions/rolesAndPermissionsSaga';
import { resetRoleClaim } from 'app/features/roles-and-permissions/roleAndPermissionsSlice';
import { transformMenuDetailToTabsConfig } from 'components/role-and-permissions/utils/transformMenuDetail';
import {
  buildRolePayload,
  buildRoleClaimPayload,
  buildFormValues,
} from 'components/role-and-permissions/utils/Payload';

const steps = [
  {
    key: 'role-information',
    label: 'Role Information',
    schema: roleInformationSchema,
    element: RoleInformationForm,
    errorKey: ['roleName', 'description', 'status'],
  },
  {
    key: 'permission-claim',
    label: 'Permission Claim',
    schema: null,
    element: RolePermissionForm,
    errorKey: [],
  },
];

function UpdateRole() {
  const { roleId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { menus, menuDetails, show, update, roleClaim } = useSelector(
    (state) => state.rolesAndPermissions,
  );
  const roleData = show?.data;

  const [tabIndex, setTabIndex] = useState(0);

  const methods = useForm({
    defaultValues: {
      roleName: '',
      description: '',
      status: 'active',
    },
  });

  const { activeStep, nextStep, previousStep } = useStepper(steps.length);

  useEffect(() => {
    dispatch(getRoleMenus('MENU'));
    if (roleId) {
      dispatch(getRoleAndPermission(roleId));
      dispatch(getMenuDetails(roleId));
    }
  }, [dispatch, roleId]);

  useEffect(() => {
    if (!roleData?.id) return;
    const values =
      menuDetails?.data?.length > 0
        ? buildFormValues(roleData, menuDetails.data)
        : {
            roleName: roleData.name,
            description: roleData.description || '',
            status: roleData.isActive ? 'active' : 'inactive',
          };
    methods.reset(values);
  }, [roleData?.id, menuDetails?.data, methods]);

  useEffect(() => {
    if (update?.isUpdated && roleData?.id) {
      nextStep();
      dispatch(getMenuDetails(roleData.id));
    }
    if (roleClaim?.success) {
      navigate(buildRoute('roles-and-permissions.index'));
      dispatch(resetRoleClaim());
    }
  }, [update?.isUpdated, roleClaim?.success, roleData?.id, nextStep, dispatch, navigate]);

  const handleStepSubmit = (stepIndex, data) => {
    if (stepIndex === 0 && roleData?.id) {
      dispatch(updateRolesAndPermissions({ ...buildRolePayload(data), id: roleData.id }));
    }
  };

  const onSubmit = (data) => {
    const id = roleData?.id;
    if (!id) return;
    dispatch(createRoleClaim(buildRoleClaimPayload(id, data)));
  };

  const tabsConfig = transformMenuDetailToTabsConfig(menuDetails?.data || []);

  const handleCancel = () => {
    navigate(buildRoute('roles-and-permissions.index'));
  };

  if (show?.isLoading && !roleData) {
    return (
      <PageContent title="Edit Role">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress />
        </Box>
      </PageContent>
    );
  }

  return (
    <PageContent title="Edit Role">
      <RolesAndPermissionForm
        steps={steps}
        methods={methods}
        onSubmit={onSubmit}
        isAddMode={false}
        isSubmitting={update?.isUpdating}
        onCancel={handleCancel}
        activeStep={activeStep}
        nextStep={nextStep}
        previousStep={previousStep}
        onStepSubmit={handleStepSubmit}
        stepProps={{
          0: { menus: menus?.data || [], menusLoading: menus?.isLoading },
          1: {
            tabsConfig,
            tabsLoading: menuDetails?.isLoading,
            activeTab: tabIndex,
            onTabChange: setTabIndex,
          },
        }}
      />
    </PageContent>
  );
}

export default UpdateRole;
